import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { importBook, importPdf, importKoboExport, importKindleClippings, importReadwise, ApiError } from '../lib/api'
import { trackImportStarted, trackImportCompleted, trackImportFailed, trackPaywallViewed, ImportSource } from '../lib/analyticsEvents'
import type { OpenLibraryBook } from '../types/models'
import { searchBooks } from '../lib/catalogSearch'
import { useSubscription } from './useSubscription'

export type Mode = 'search' | 'manual' | 'highlights' | 'pdf' | 'kobo' | 'kindle-clippings' | 'readwise'

export interface ChapterDraft {
  title: string
  highlightText: string
}

export function useAddBook() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // ?q= from the taste widget seeds the search; auto-selects only on an exact match in the first batch.
  const initialQuery = searchParams.get('q')?.trim() ?? ''
  const { subscription, loading: subLoading } = useSubscription()
  // Catalog adds are free; file imports and extra books are Premium.
  const isFree = !subLoading && subscription?.active !== true
  const [upgradeOpen, setUpgradeOpen] = useState(false)

  // Also logs paywall_viewed. Web never sells, so this is the only upgrade surface.
  function openUpgrade(trigger: string) {
    trackPaywallViewed(trigger)
    setUpgradeOpen(true)
  }

  function handleImportError(e: unknown, fallback: string, source: ImportSource) {
    if (e instanceof ApiError && e.code === 'PAYMENT_REQUIRED') {
      openUpgrade('import')
    } else {
      setError(e instanceof Error ? e.message : fallback)
      trackImportFailed(source, e instanceof Error ? e.message : fallback)
    }
    setSubmitting(false)
  }

  const [mode, setMode] = useState<Mode>('search')
  const [title, setTitle] = useState('')
  const [author, setAuthor] = useState('')
  const [query, setQuery] = useState(initialQuery)
  const [results, setResults] = useState<OpenLibraryBook[]>([])
  const [searching, setSearching] = useState(initialQuery.length >= 3)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [selectedBook, setSelectedBook] = useState<OpenLibraryBook | null>(null)
  const [trackedQuery, setTrackedQuery] = useState(query)
  const [chapters, setChapters] = useState<ChapterDraft[]>([{ title: '', highlightText: '' }])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [koboFile, setKoboFile] = useState<File | null>(null)
  const [koboResult, setKoboResult] = useState<{ books: number; passages: number } | null>(null)
  const [kindleFile, setKindleFile] = useState<File | null>(null)
  const [kindleResult, setKindleResult] = useState<{ books: number; passages: number } | null>(null)
  const [readwiseFile, setReadwiseFile] = useState<File | null>(null)
  const [readwiseResult, setReadwiseResult] = useState<{ books: number; passages: number } | null>(null)
  const [autoSelectDone, setAutoSelectDone] = useState(initialQuery.length === 0)

  // Synchronous during render, not in an effect (react-hooks/set-state-in-effect).
  if (query !== trackedQuery) {
    setTrackedQuery(query)
    setSearchError(null)
    if (query.length < 3) {
      setResults([])
      setSearching(false)
    } else {
      setSearching(true)
    }
  }

  async function runSearch(q: string) {
    try {
      setResults(await searchBooks(q))
      setSearchError(null)
    } catch {
      setResults([])
      setSearchError("Couldn't reach the catalogue.")
    } finally {
      setSearching(false)
    }
  }

  useEffect(() => {
    if (query.length < 3) return
    const id = setTimeout(() => { runSearch(query) }, 300)
    return () => clearTimeout(id)
  }, [query])

  function retrySearch() {
    setSearching(true)
    runSearch(query)
  }

  function handleSelectBook(book: OpenLibraryBook) {
    setSelectedBook(book)
    setTitle(book.title)
    setAuthor(book.author_name?.[0] ?? '')
    setResults([])
    setQuery('')
    setSearchError(null)
  }

  // One-shot during render; state, not a ref, so it can be read here.
  if (!autoSelectDone && results.length > 0) {
    setAutoSelectDone(true)
    const match = results.find(r => r.title.trim().toLowerCase() === initialQuery.toLowerCase())
    if (match) handleSelectBook(match)
  }

  async function handleGenerateQuestions() {
    setError(null)
    setSubmitting(true)
    trackImportStarted(ImportSource.CATALOG)
    try {
      const result = await importBook({ title: title.trim(), author: author.trim(), chapters: [], source: 'web_catalog', generate_questions: true })
      trackImportCompleted(ImportSource.CATALOG, 0, 1)
      navigate(`/library/${result.bookId}`)
    } catch (e) {
      handleImportError(e, 'Failed to import book', ImportSource.CATALOG)
    }
  }

  function addChapter() { setChapters(prev => [...prev, { title: '', highlightText: '' }]) }
  function removeChapter(index: number) { if (chapters.length > 1) setChapters(prev => prev.filter((_, i) => i !== index)) }
  function updateChapter(index: number, field: keyof ChapterDraft, value: string) {
    setChapters(prev => prev.map((ch, i) => i === index ? { ...ch, [field]: value } : ch))
  }

  async function handlePdfImport(e: React.FormEvent) {
    e.preventDefault()
    if (!pdfFile || !title.trim() || !author.trim()) return
    setError(null)
    setSubmitting(true)
    trackImportStarted(ImportSource.PDF)
    try {
      const result = await importPdf(pdfFile, title.trim(), author.trim())
      trackImportCompleted(ImportSource.PDF, 0, 1)
      navigate(`/library/${result.bookId}`)
    } catch (e) {
      handleImportError(e, 'Failed to import PDF', ImportSource.PDF)
    }
  }

  async function handleKoboImport(e: React.FormEvent) {
    e.preventDefault()
    if (!koboFile) return
    setError(null)
    setSubmitting(true)
    trackImportStarted(ImportSource.KOBO)
    try {
      const results = await importKoboExport(koboFile)
      const totalPassages = results.reduce((sum, r) => sum + r.highlightCount, 0)
      trackImportCompleted(ImportSource.KOBO, totalPassages, results.length)
      setKoboResult({ books: results.length, passages: totalPassages })
      setSubmitting(false)
    } catch (e) {
      handleImportError(e, 'Failed to import Kobo export', ImportSource.KOBO)
    }
  }

  async function handleKindleClippingsImport(e: React.FormEvent) {
    e.preventDefault()
    if (!kindleFile) return
    setError(null); setSubmitting(true)
    trackImportStarted(ImportSource.KINDLE)
    try {
      const results = await importKindleClippings(kindleFile)
      const totalPassages = results.reduce((sum, r) => sum + r.highlightCount, 0)
      trackImportCompleted(ImportSource.KINDLE, totalPassages, results.length)
      setKindleResult({ books: results.length, passages: totalPassages })
      setSubmitting(false)
    } catch (e) {
      handleImportError(e, 'Failed to import Kindle highlights', ImportSource.KINDLE)
    }
  }

  async function handleReadwiseImport(e: React.FormEvent) {
    e.preventDefault()
    if (!readwiseFile) return
    setError(null); setSubmitting(true)
    trackImportStarted(ImportSource.READWISE)
    try {
      const results = await importReadwise(readwiseFile)
      const totalPassages = results.reduce((sum, r) => sum + r.highlightCount, 0)
      trackImportCompleted(ImportSource.READWISE, totalPassages, results.length)
      setReadwiseResult({ books: results.length, passages: totalPassages })
      setSubmitting(false)
    } catch (e) {
      handleImportError(e, 'Failed to import Readwise export', ImportSource.READWISE)
    }
  }

  async function handleImport(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    const importSource = mode === 'manual' ? ImportSource.MANUAL : ImportSource.CATALOG
    const preparedChapters = chapters
      .filter(ch => ch.title.trim() || ch.highlightText.trim())
      .map(ch => ({
        title: ch.title.trim() || 'Chapter',
        highlights: ch.highlightText.split('\n').filter(l => l.trim()).map(text => ({ text: text.trim() })),
      }))
    const highlightCount = preparedChapters.reduce((sum, ch) => sum + ch.highlights.length, 0)
    trackImportStarted(importSource)
    try {
      const result = await importBook({
        title: title.trim(),
        author: author.trim(),
        chapters: preparedChapters,
        source: mode === 'manual' ? 'web_manual' : 'web_catalog',
        generate_questions: true,
      })
      trackImportCompleted(importSource, highlightCount, 1)
      navigate(`/library/${result.bookId}`)
    } catch (e) {
      handleImportError(e, 'Failed to import book', importSource)
    }
  }

  return {
    mode,
    setMode,
    title,
    setTitle,
    author,
    setAuthor,
    query,
    setQuery,
    results,
    searching,
    searchError,
    selectedBook,
    setSelectedBook,
    chapters,
    submitting,
    error,
    setError,
    pdfFile,
    setPdfFile,
    koboFile,
    setKoboFile,
    koboResult,
    setKoboResult,
    kindleFile,
    setKindleFile,
    kindleResult,
    setKindleResult,
    readwiseFile,
    setReadwiseFile,
    readwiseResult,
    setReadwiseResult,
    isFree,
    upgradeOpen,
    setUpgradeOpen,
    openUpgrade,
    retrySearch,
    handleSelectBook,
    handleGenerateQuestions,
    addChapter,
    removeChapter,
    updateChapter,
    handlePdfImport,
    handleKoboImport,
    handleKindleClippingsImport,
    handleReadwiseImport,
    handleImport,
  }
}

export type AddBookState = ReturnType<typeof useAddBook>
